import { runSuiteLabels } from './field-object-labels';

import type { FieldSection } from './field-view';
import type { FieldActorId, FieldSnapshot } from '@mio/shared';

export interface FieldDestination {
  section: FieldSection;
  actorId?: FieldActorId;
  meetingId?: string;
  focusId?: string | undefined;
  draft?: string;
  sourceIds?: string[];
}

export interface FieldSearchResult extends FieldDestination {
  id: string;
  title: string;
  detail: string;
}

/** Search the learner's existing read model; never create or infer company evidence. */
export function searchField(snapshot: FieldSnapshot, query: string): FieldSearchResult[] {
  const needle = query.trim().toLocaleLowerCase();
  if (!needle) return [];
  const entries: FieldSearchResult[] = [
    ...(snapshot.discovery
      ? [
          {
            id: `brief:${snapshot.discovery.brief.id}`,
            title: snapshot.discovery.brief.title,
            detail: 'Project brief',
            section: 'engagement' as const,
            focusId: snapshot.discovery.brief.id,
          },
        ]
      : []),
    ...(snapshot.discovery?.decisions.map((decision) => ({
      id: `decision:${decision.id}`,
      title: decision.title,
      detail: `Decision · ${decision.status} · version ${decision.version}`,
      section: 'engagement' as const,
      focusId: decision.id,
    })) ?? []),
    ...(snapshot.discovery?.findings.map((finding) => ({
      id: `finding:${finding.id}`,
      title: finding.title,
      detail: `Finding · ${finding.category.replaceAll('_', ' ')} · ${finding.status}`,
      section: 'engagement' as const,
      focusId: finding.id,
    })) ?? []),
    ...snapshot.actors.map((actor) => ({
      id: `actor:${actor.id}`,
      title: actor.name,
      detail: actor.role,
      section: 'inbox' as const,
      actorId: actor.id,
    })),
    ...snapshot.tickets.map((ticket) => ({
      id: `ticket:${ticket.id}`,
      title: ticket.title,
      detail: `Ticket · ${ticket.id}`,
      section: 'board' as const,
      focusId: ticket.id,
    })),
    ...snapshot.prs.map((pr) => ({
      id: `pr:${pr.id}`,
      title: pr.title,
      detail: 'Pull request',
      section: 'reviews' as const,
      focusId: pr.id,
    })),
    ...snapshot.documents.map((document) => ({
      id: `doc:${document.id}`,
      title: document.title,
      detail: document.author === 'learner' ? 'Your delivery' : 'Reference document',
      section: 'documents' as const,
      focusId: document.id,
    })),
    ...snapshot.runs.map((run) => ({
      id: `run:${run.id}`,
      title: runSuiteLabels[run.suite],
      detail: `${run.status} · ${snapshot.prs.find((pr) => pr.id === run.prId)?.title ?? 'Code revision'}`,
      section: 'operations' as const,
      focusId: run.id,
    })),
  ];
  return entries
    .filter((item) => `${item.title} ${item.detail}`.toLocaleLowerCase().includes(needle))
    .slice(0, 30);
}

export function fieldToday(snapshot: FieldSnapshot) {
  return {
    activeTickets: snapshot.tickets.filter(
      (ticket) =>
        ticket.status !== 'done' &&
        ticket.status !== 'deferred' &&
        (ticket.day <= snapshot.engagement.day || ticket.status !== 'backlog'),
    ),
    running: snapshot.operations.filter(
      (operation) => operation.status === 'pending' || operation.status === 'running',
    ),
    failed: snapshot.operations.filter((operation) => operation.status === 'failed'),
    openIncidents: snapshot.incidents.filter((incident) => incident.status !== 'resolved'),
    reviewRequests: snapshot.prs.filter(
      (pr) =>
        pr.status === 'open' &&
        snapshot.actors.some((actor) => {
          const latest = pr.reviews
            .filter((review) => review.actorId === actor.id && review.revision === pr.revision)
            .sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0];
          return latest?.status === 'changes_requested';
        }),
    ),
    done: snapshot.tickets.filter((ticket) => ticket.status === 'done').length,
  };
}
